/** Matches ProductForm productName validation (letters required; no brackets/parentheses). */
const PRODUCT_NAME_LETTER_RE = /[a-zA-ZäöüßÄÖÜ]/;
const PRODUCT_NAME_MAX_LEN = 50;

export const PRODUCT_NAME_PATTERN =
  /^(?=.*[a-zA-Z\u00E4\u00F6\u00FC\u00DF\u00C4\u00D6\u00DC])[a-zA-Z\u00E4\u00F6\u00FC\u00DF\u00C4\u00D6\u00DC0-9_\s.-]{1,50}$/;

export function isValidProductName(value: string): boolean {
  return PRODUCT_NAME_PATTERN.test(value.trim());
}

/**
 * Derives a valid product name from a model filename (extension stripped, invalid chars removed).
 */
export function productNameFromModelFilename(filename: string): string {
  const withoutExtension = filename.replace(/\.[^/.]+$/, '').trim();
  if (!withoutExtension) return '';

  const sanitized = withoutExtension
    .replace(/[^a-zA-ZäöüßÄÖÜ0-9_\s.-]/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, PRODUCT_NAME_MAX_LEN)
    .trim();

  return PRODUCT_NAME_LETTER_RE.test(sanitized) ? sanitized : '';
}

/**
 * Ensures a unique display name among drafts (case-insensitive).
 * Falls back to `Product N` when the filename cannot produce a valid name.
 */
export function uniqueProductName(
  desired: string,
  taken: string[],
  fallbackIndex: number
): string {
  const trimmed = desired.trim();
  const seed =
    trimmed && PRODUCT_NAME_LETTER_RE.test(trimmed)
      ? trimmed.slice(0, PRODUCT_NAME_MAX_LEN).trim()
      : `Product ${fallbackIndex}`;

  const takenLower = new Set(taken.map((name) => name.toLowerCase()));
  if (!takenLower.has(seed.toLowerCase())) return seed;

  let n = 2;
  while (n < 1000) {
    const suffix = ` ${n}`;
    const maxBaseLen = Math.max(1, PRODUCT_NAME_MAX_LEN - suffix.length);
    const candidate = `${seed.slice(0, maxBaseLen)}${suffix}`;
    if (!takenLower.has(candidate.toLowerCase())) return candidate;
    n += 1;
  }

  return seed.slice(0, PRODUCT_NAME_MAX_LEN);
}
